import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import {
  AddCommentRequest,
  CommentEntityType,
  CommentThreadType,
} from "../../../api/comments";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
  selectAllComments,
  selectCommentsIsAdding,
} from "../logic/commentsSlice";
import { addCommentThunk, fetchCommentsThunk } from "../logic/commentsThunk";
import CommentComposer from "./trail/CommentComposer";
import CommentsList from "./trail/CommentsList";

export interface CommentsTrailProps {
  case_id: string;
  thread_type: CommentThreadType;
  entity_type: CommentEntityType;
  entity_id: string;
  onClose: () => void;
  currentUserName?: string;
  skipInitialFetch?: boolean;
  maxHeightPx?: number;
}

export const CommentsTrail: React.FC<CommentsTrailProps> = ({
  case_id,
  thread_type,
  entity_id,
  entity_type,
  currentUserName,
  skipInitialFetch = false,
  maxHeightPx,
}) => {
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const commentsListRef = useRef<HTMLDivElement | null>(null);
  const commentsScrollRef = useRef<HTMLDivElement | null>(null);

  const comments = useAppSelector(selectAllComments);

  const [commentDraft, setCommentDraft] = useState("");
  const isSubmitting = useAppSelector(selectCommentsIsAdding);
  const [now, setNow] = useState(() => new Date());

  const getNextTickMs = (commentedAtIso: string, currentNow: Date) => {
    const ts = Date.parse(commentedAtIso);
    if (!Number.isFinite(ts)) return null;
    const ageMs = currentNow.getTime() - ts;
    if (ageMs < 0) return 10_000;

    if (ageMs < 60_000) {
      const step = 10_000;
      return Math.max(250, step - (ageMs % step));
    }

    if (ageMs < 60 * 60_000) {
      const step = 60_000;
      return Math.max(250, step - (ageMs % step));
    }

    if (ageMs < 24 * 60 * 60_000) {
      const step = 60 * 60_000;
      return Math.max(250, step - (ageMs % step));
    }

    return null;
  };

  const sortedComments = useMemo(() => {
    const toTime = (value: string) => {
      const t = Date.parse(value);
      return Number.isFinite(t) ? t : 0;
    };

    return [...comments].sort(
      (a, b) => toTime(b.commented_at) - toTime(a.commented_at),
    );
  }, [comments]);

  useEffect(() => {
    let nextMs: number | null = null;
    for (const c of sortedComments) {
      const tick = getNextTickMs(c.commented_at, now);
      if (tick == null) continue;
      nextMs = nextMs == null ? tick : Math.min(nextMs, tick);
    }

    if (nextMs == null) return;

    const id = window.setTimeout(() => setNow(new Date()), nextMs);
    return () => window.clearTimeout(id);
  }, [now, sortedComments]);

  useEffect(() => {
    if (!skipInitialFetch) {
      dispatch(
        fetchCommentsThunk({
          case_id,
          thread_type,
          entity_type,
          entity_id,
        }),
      );
    }
  }, [dispatch, case_id, thread_type, entity_type, entity_id, skipInitialFetch]);

  const resolvedCurrentUserName = useMemo(() => {
    if (currentUserName) return currentUserName;
    const name = auth?.user?.profile?.name as string | undefined;
    if (name && name.trim()) return name.trim();

    return "User";
  }, [auth?.user?.profile?.email, auth?.user?.profile?.name, currentUserName]);

  const onAddComment = async () => {
    if (!commentDraft.trim()) return;

    try {
      const request: AddCommentRequest = {
        thread_type,
        entity_type,
        entity_id,
        comment: commentDraft.trim(),
      };

      await dispatch(
        addCommentThunk({
          case_id,
          request,
          commentor: resolvedCurrentUserName,
        }),
      ).unwrap();

      setCommentDraft("");

      requestAnimationFrame(() => {
        const el = maxHeightPx ? commentsScrollRef.current : commentsListRef.current;
        el?.scrollTo({ top: 0, behavior: "smooth" });
      });
      setNow(new Date());
    } catch {}
  };

  const onCancel = () => {
    setCommentDraft("");
  };

  const renderComposer = () => {
    return (
      <div className="px-2 pt-4 pb-2">
        <CommentComposer
          currentUserName={resolvedCurrentUserName}
          value={commentDraft}
          onChange={setCommentDraft}
          onCancel={onCancel}
          onSubmit={onAddComment}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  };

  const renderComments = () => {
    if (maxHeightPx) {
      return (
        <div
          ref={commentsScrollRef}
          className="overflow-y-auto"
          style={{ maxHeight: maxHeightPx }}
        >
          <CommentsList comments={sortedComments} now={now} disableScroll />
        </div>
      );
    }

    return (
      <div className="flex-1 min-h-0">
        <CommentsList
          comments={sortedComments}
          containerRef={commentsListRef}
          now={now}
        />
      </div>
    );
  };

  const renderDivider = () => {
    return (
      <div className="w-full flex justify-center">
        <div className="flex self-center w-full border-t border-[#E5E5E5]" />
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {renderComposer()}
      {sortedComments.length > 0 && renderDivider()}
      {sortedComments.length > 0 && renderComments()}
    </div>
  );
};
